import React from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Space, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// One Employee History entry's fields (EmployeeAssignmentHistory), shared by
// the Employee History page and the panel WFM Skills Assign opens from Sched Hrs.

export const STATUSES = [
  'Accepted Offer', 'Rescinded Offer', 'No Show', 'Orientation Fallout',
  'In Training', 'Former Trainee', 'In Production', 'Former Employee', 'Inter Transfer',
];

export const STATUS_COLOR = {
  'Accepted Offer':    'cyan',
  'Rescinded Offer':   'gold',
  'No Show':           'default',
  'Orientation Fallout': 'volcano',
  'In Training':       'blue',
  'Former Trainee':    'orange',
  'In Production':     'green',
  'Former Employee':   'red',
  'Inter Transfer':    'purple',
};

export const STATUS_TOOLTIP = {
  'Rescinded Offer':    'Abandoned the job before the hire date',
  'No Show':            'Did not attend orientation, termed',
  'Orientation Fallout': 'Did not attend day after orientation, termed',
  'Inter Transfer':     'Switched to new team or position',
};

// An entry as the form's values.
export function historyFormValues(record) {
  return {
    UserID:         record.UserID,
    TeamID:         record.TeamID,
    JobID:          record.JobID ?? undefined,
    StartDate:      record.StartDate      ? dayjs(record.StartDate)      : null,
    EndDate:        record.EndDate        ? dayjs(record.EndDate)        : null,
    Status:               record.Status,
    GraduationDate:       record.GraduationDate ? dayjs(record.GraduationDate) : null,
    Notes:                record.Notes ?? '',
    TransferredIn:        !!record.TransferredIn,
    TrainingNonBillable:  !!record.TrainingNonBillable,
    Referral:             !!record.Referral,
    ScheduledHours:       record.ScheduledHours ?? 40,
    IgnoreInRecruitment:  !!record.IgnoreInRecruitment,
  };
}

// The form's values as the body for POST / PUT /api/employee-history.
export function historyPayload(values) {
  return {
    UserID:         values.UserID,
    TeamID:         values.TeamID,
    JobID:          values.JobID ?? null,
    StartDate:      values.StartDate?.format('YYYY-MM-DD') ?? null,
    EndDate:        values.EndDate?.format('YYYY-MM-DD') ?? null,
    Status:         values.Status,
    GraduationDate: values.GraduationDate?.format('YYYY-MM-DD') ?? null,
    Notes:               values.Notes || null,
    TransferredIn:       values.TransferredIn ? 1 : 0,
    TrainingNonBillable: values.TrainingNonBillable ? 1 : 0,
    Referral:            values.Referral ? 1 : 0,
    ScheduledHours:      values.ScheduledHours ?? 40,
    IgnoreInRecruitment: values.IgnoreInRecruitment ? 1 : 0,
  };
}

// The Form.Items, inside the caller's <Form>. users: the Employee picker's
// options; lockEmployee: an existing entry, whose employee cannot change;
// editable: if given, only these fields can be changed, the rest are shown
// read-only; hoursFirst: Scheduled Hours and Notes right under Job.
export default function EmployeeHistoryFields({ users, teams, jobs, lockEmployee, editable, hoursFirst }) {
  const dis = (name) => (editable ? !editable.includes(name) : undefined);
  const hours = (
    <Form.Item name="ScheduledHours" label="Scheduled Hours / Week" rules={[{ required: true, message: 'Required' }]}>
      <InputNumber disabled={dis('ScheduledHours')} min={1} max={40} style={{ width: 120 }} placeholder="40" />
    </Form.Item>
  );
  const notes = (
    <Form.Item name="Notes" label="Notes">
      <Input.TextArea disabled={dis('Notes')} rows={2} placeholder="Optional notes" />
    </Form.Item>
  );
  return (
    <>
      <Form.Item name="UserID" label="Employee" rules={[{ required: true, message: 'Employee is required' }]}>
        <Select
          showSearch optionFilterProp="label"
          options={users.map((u) => ({ value: u.ID, label: u.DisplayName }))}
          placeholder="Select employee"
          disabled={lockEmployee || dis('UserID')}
        />
      </Form.Item>
      <Form.Item name="TeamID" label="Team" rules={[{ required: true, message: 'Team is required' }]}>
        <Select disabled={dis('TeamID')}
          showSearch optionFilterProp="label"
          options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
          placeholder="Select team"
        />
      </Form.Item>
      <Form.Item name="JobID" label="Job">
        <Select disabled={dis('JobID')}
          showSearch optionFilterProp="label" allowClear
          options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))}
          placeholder="Select job (optional)"
        />
      </Form.Item>
      {hoursFirst && hours}
      {hoursFirst && notes}
      <Form.Item name="Status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
        <Select disabled={dis('Status')}
          placeholder="Select status"
          options={STATUSES.map((s) => ({ value: s, label: s }))}
          optionRender={(opt) =>
            STATUS_TOOLTIP[opt.value] ? (
              <Tooltip title={STATUS_TOOLTIP[opt.value]} placement="right">
                <span style={{ display: 'block' }}>{opt.label}</span>
              </Tooltip>
            ) : opt.label
          }
        />
      </Form.Item>
      <Form.Item name="StartDate" label="Start Date" rules={[{ required: true, message: 'Start date is required' }]}>
        <DatePicker disabled={dis('StartDate')} style={{ width: '100%' }} format={['YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY']} />
      </Form.Item>
      <Form.Item
        name="GraduationDate"
        label="Graduation Date"
        dependencies={['StartDate']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value) return Promise.resolve();
              const start = getFieldValue('StartDate');
              if (start && value.isBefore(start, 'day'))
                return Promise.reject(new Error('Graduation date cannot be before start date'));
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker disabled={dis('GraduationDate')} style={{ width: '100%' }} format={['YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY']} />
      </Form.Item>
      <Form.Item
        name="EndDate"
        label="End Date"
        dependencies={['StartDate', 'GraduationDate']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value) return Promise.resolve();
              const start = getFieldValue('StartDate');
              if (start && value.isBefore(start, 'day'))
                return Promise.reject(new Error('End date cannot be before start date'));
              const grad = getFieldValue('GraduationDate');
              if (grad && value.isBefore(grad, 'day'))
                return Promise.reject(new Error('End date cannot be before graduation date'));
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker disabled={dis('EndDate')} style={{ width: '100%' }} format={['YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY']} placeholder="Leave blank if current" />
      </Form.Item>
      <Form.Item
        name="TrainingNonBillable"
        valuePropName="checked"
        label={
          <Space size={4}>
            Training Non-Billable
            <Tooltip title="When set, this person's billable hours start the day after their Graduation Date instead of using the class-level Billable After Days offset">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
      >
        <Switch disabled={dis('TrainingNonBillable')} />
      </Form.Item>
      <Form.Item name="Referral" valuePropName="checked" label="Referral">
        <Switch disabled={dis('Referral')} />
      </Form.Item>
      {!hoursFirst && hours}
      <Form.Item
        name="IgnoreInRecruitment"
        valuePropName="checked"
        label={
          <Space size={4}>
            Ignore in Recruitment
            <Tooltip title="Check this when the record is a schedule change only, not a real recruitment class entry. Excluded from Grad %, Order Fill %, and class stats.">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
      >
        <Switch disabled={dis('IgnoreInRecruitment')} />
      </Form.Item>
      <Form.Item
        name="TransferredIn"
        valuePropName="checked"
        label={
          <Space size={4}>
            Transferred In
            <Tooltip title="When an employee moves to a new job or team, set this flag on their new job/team entry">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
      >
        <Switch disabled={dis('TransferredIn')} />
      </Form.Item>
      {!hoursFirst && notes}
    </>
  );
}
